import { useState, useEffect } from 'react';
import { X, Lock, Unlock, Eye, EyeOff } from 'lucide-react';
import { supabase, waitForSupabaseAuthReady } from '../lib/supabase';
import { auth } from '../lib/auth';
import { useTheme } from '../lib/theme-provider';
import { ThemedCard } from './ThemedCard';
import { ThemedButton } from './ThemedButton';

interface CreateRoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateSuccess?: (roomId: string) => void;
}

export default function CreateRoomModal({ isOpen, onClose, onCreateSuccess }: CreateRoomModalProps) {
  const { theme } = useTheme();
  const [roomName, setRoomName] = useState('');
  const [roomPassword, setRoomPassword] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (roomPassword.length > 0 && !isPrivate) {
      setIsPrivate(true);
    }
  }, [roomPassword]);

  const handleTogglePrivate = (privateValue: boolean) => {
    if (privateValue && roomPassword.length === 0) {
      setError('私密房间必须设置 6 位密码');
      return;
    }
    setIsPrivate(privateValue);
    if (!privateValue) {
      setRoomPassword('');
      setError('');
    }
  };

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    if (!roomName.trim()) {
      setError('房间名称不能为空');
      return false;
    }
    if (roomName.trim().length < 1) {
      setError('房间名称至少需要 1 个字符');
      return false;
    }
    if (isPrivate && roomPassword.length !== 6) {
      setError('房间密码必须是 6 位数字');
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      await waitForSupabaseAuthReady();
      
      const user = auth.getCurrentUser();
      if (!user) {
        setError('请先登录');
        setLoading(false);
        return;
      }

      const { data: playerProfile, error: profileError } = await supabase.rpc(
        'get_or_create_player_profile',
        { p_nickname: user.nickname }
      );

      if (profileError || !playerProfile) {
        console.error('[CreateRoomModal] 获取玩家资料失败:', profileError);
        setError('获取玩家资料失败，请重试');
        setLoading(false);
        return;
      }

      const playerProfileData = playerProfile as any;
      const playerId = playerProfileData.player_number || playerProfileData.id;

      const { data: allRooms } = await supabase
        .from('online_game_rooms_cce0d86f_8f5')
        .select('id')
        .eq('is_deleted', 'n')
        .order('id', { ascending: false })
        .limit(1);

      const nextId = (allRooms && allRooms.length > 0 ? allRooms[0].id : 0) + 1;

      const { data: existingRooms } = await supabase
        .from('online_game_rooms_cce0d86f_8f5')
        .select('room_name')
        .eq('room_name', roomName.trim())
        .eq('is_deleted', 'n');

      if (existingRooms && existingRooms.length > 0) {
        setError('房间名称已存在，请使用其他名称');
        setLoading(false);
        return;
      }

      // 生成房间ID字符串
      const roomIdStr = 'ROOM_' + new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14) + '_' + Math.floor(Math.random() * 10000).toString().padStart(4, '0');

      // 直接插入房间数据（使用实际表结构）
      const { data: newRoom, error: createError } = await supabase
        .from('online_game_rooms_cce0d86f_8f5')
        .insert({
          room_code: roomIdStr,
          room_name: roomName.trim(),
          host_player_number: playerId,
          max_player_count: 10,
          is_public: !isPrivate,
          room_status: 'waiting',
          status: 'waiting',
          is_deleted: 'n'
        })
        .select()
        .single();

      if (createError) {
        console.error('[CreateRoomModal] 创建房间失败:', createError);
        setError('创建房间失败，请稍后重试');
        setLoading(false);
        return;
      }

      if (!newRoom) {
        setError('创建房间失败，未返回房间数据');
        setLoading(false);
        return;
      }

      // 房主自动加入房间
      const { error: joinError } = await supabase
        .from('online_room_players_cce0d86f_8f5')
        .insert({
          room_id: newRoom.id,
          player_id: playerId,
          player_nickname: user.nickname,
          seat_number: 1,
          player_role: 'host',
          is_spectator: false,
          is_host: true,
          is_deleted: 'n'
        });

      if (joinError) {
        console.error('[CreateRoomModal] 房主加入房间失败:', joinError);
        // 继续，因为房间已创建成功
      }

      onCreateSuccess?.(newRoom.id.toString());
      handleClose();
    } catch (err) {
      console.error('[CreateRoomModal] 创建房间异常:', err);
      setError('创建房间失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRoomName('');
    setRoomPassword('');
    setIsPrivate(false);
    setShowPassword(false);
    setError('');
    onClose();
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setRoomPassword(value);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      onClick={handleClose}
    >
      <ThemedCard 
        className="w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 
            className="text-lg font-semibold"
            style={{ color: theme.colors['text-primary'] }}
          >
            创建房间
          </h3>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
            style={{ 
              backgroundColor: `${theme.colors['bg-primary']}50`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors['bg-primary'];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = `${theme.colors['bg-primary']}50`;
            }}
          >
            <X className="w-4 h-4" style={{ color: theme.colors['text-primary'] }} />
          </button>
        </div>

        <div className="space-y-4">
          {/* 房间名称 */}
          <div>
            <label 
              className="block text-sm mb-2"
              style={{ color: theme.colors['text-secondary'] }}
            >
              房间名称 <span style={{ color: theme.colors['accent-red'] }}>*</span>
            </label>
            <input
              type="text"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              placeholder="请输入房间名称"
              maxLength={20}
              className="w-full px-4 py-2.5 rounded-lg outline-none transition-colors"
              style={{
                backgroundColor: theme.colors['bg-primary'],
                border: `1px solid ${theme.colors['border-secondary']}30`,
                color: theme.colors['text-primary'],
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = theme.colors['border-primary'];
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = `${theme.colors['border-secondary']}30`;
              }}
            />
          </div>

          {/* 房间密码 */}
          <div>
            <label 
              className="block text-sm mb-2"
              style={{ color: theme.colors['text-secondary'] }}
            >
              房间密码 <span style={{ color: theme.colors['accent-red'] }}>*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={roomPassword}
                onChange={handlePasswordChange}
                placeholder="输入 6 位密码"
                className="w-full px-4 py-2.5 pr-12 rounded-lg outline-none transition-colors"
                style={{
                  backgroundColor: theme.colors['bg-primary'],
                  border: `1px solid ${theme.colors['border-secondary']}30`,
                  color: theme.colors['text-primary'],
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.colors['border-primary'];
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = `${theme.colors['border-secondary']}30`;
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: theme.colors['text-secondary'] }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = theme.colors['text-primary'];
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = theme.colors['text-secondary'];
                }}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            <p 
              className="text-xs mt-1.5"
              style={{ color: theme.colors['text-muted'] }}
            >
              {roomPassword.length}/6
            </p>
          </div>

          {/* 房间类型 */}
          <div>
            <div className="flex gap-2">
              <button
                onClick={() => handleTogglePrivate(false)}
                className="flex-1 py-2 rounded-lg border transition-all flex items-center justify-center gap-1.5 text-xs"
                style={{
                  backgroundColor: !isPrivate ? theme.colors['accent-gold'] : theme.colors['bg-primary'],
                  color: !isPrivate ? theme.colors['bg-primary'] : theme.colors['text-secondary'],
                  borderColor: !isPrivate ? theme.colors['accent-gold'] : `${theme.colors['border-secondary']}30`,
                }}
              >
                <Unlock className="w-3.5 h-3.5" />
                <span>公开房间</span>
              </button>
              <button
                onClick={() => handleTogglePrivate(true)}
                className="flex-1 py-2 rounded-lg border transition-all flex items-center justify-center gap-1.5 text-xs"
                style={{
                  backgroundColor: isPrivate ? theme.colors['accent-gold'] : theme.colors['bg-primary'],
                  color: isPrivate ? theme.colors['bg-primary'] : theme.colors['text-secondary'],
                  borderColor: isPrivate ? theme.colors['accent-gold'] : `${theme.colors['border-secondary']}30`,
                }}
              >
                <Lock className="w-3.5 h-3.5" />
                <span>私密房间</span>
              </button>
            </div>
          </div>

          {/* 错误提示 */}
          {error && (
            <div 
              className="rounded-lg p-3 text-sm"
              style={{
                backgroundColor: `${theme.colors['accent-red']}20`,
                border: `1px solid ${theme.colors['accent-red']}30`,
                color: theme.colors['accent-red'],
              }}
            >
              {error}
            </div>
          )}

          {/* 操作按钮 */}
          <div className="flex gap-3 pt-4">
            <ThemedButton
              variant="secondary"
              onClick={handleClose}
              className="flex-1"
            >
              取消
            </ThemedButton>
            <ThemedButton
              onClick={handleCreate}
              disabled={loading}
              className="flex-1"
            >
              {loading ? '创建中...' : '创建房间'}
            </ThemedButton>
          </div>
        </div>
      </ThemedCard>
    </div>
  );
}
